import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, AlertCircle } from "lucide-react";
import { formatINR } from "../lib/format";

/**
 * Compact billing-status strip shown at the top of /account for every buyer.
 * Reads /api/invoices/mine and highlights open dues + total outstanding.
 */
export function InvoicesWidget({ api }) {
  const [state, setState] = useState({ loading: true, open: 0, due: 0, total: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/invoices/mine");
        const invs = res.data.invoices || [];
        const open = invs.filter((i) => i.status !== "paid" && i.status !== "void");
        const due = open.reduce((s, i) => s + (i.total || 0), 0);
        if (alive) setState({ loading: false, open: open.length, due, total: invs.length });
      } catch {
        if (alive) setState({ loading: false, open: 0, due: 0, total: 0 });
      }
    })();
    return () => { alive = false; };
  }, [api]);

  if (state.loading || state.total === 0) return null;

  const hasDues = state.open > 0;
  const accent = hasDues ? "#D8B23E" : "#2FB999";

  return (
    <Link
      to="/account/invoices"
      data-testid="invoices-widget"
      className="group mt-8 flex flex-wrap items-center justify-between gap-4 rounded-sm border p-5 transition-colors"
      style={{
        borderColor: `${accent}55`,
        background: `linear-gradient(90deg, ${accent}0f, transparent 60%)`,
      }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <span
          className="grid place-items-center w-11 h-11 rounded-full text-black shrink-0"
          style={{ background: accent }}
        >
          {hasDues ? <AlertCircle size={19} /> : <FileText size={19} />}
        </span>
        <div className="min-w-0">
          <div className="font-serif text-xl md:text-2xl truncate">
            {hasDues ? (
              <>
                <span data-testid="invoices-widget-count">{state.open}</span> open invoice{state.open === 1 ? "" : "s"}
                <span className="text-[#8A8A82] mx-2">·</span>
                <span style={{ color: accent }} data-testid="invoices-widget-due">
                  {formatINR(state.due)}
                </span>{" "}
                due
              </>
            ) : (
              <>All {state.total} invoice{state.total === 1 ? "" : "s"} settled.</>
            )}
          </div>
          <div className="font-body text-xs text-[#8A8A82] mt-1">
            {hasDues
              ? "Pay individually or bundle open dues — Razorpay or PhonePe, your choice."
              : "Download PDFs from your Invoice Vault any time."}
          </div>
        </div>
      </div>
      <span
        className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.22em]"
        style={{ color: accent }}
      >
        Open vault <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}
